import { EventType, setPersonProperties, trackEvent } from "@/shared/utils/core";
import type { FlowAnswers } from "@/shared/constants/flowTypes";

export type OnboardingFlow = "self_serve" | "attorney_invite" | "family_invite";
export type OnboardingEntryType = "default" | "access_code" | "obituary_link";
export type SignupMethod = "email" | "google";
export type PromptLocation =
  | "sidebar"
  | "home_see_plans"
  | "overview"
  | "estate_inventory"
  | "settle_estate"
  | "documents"
  | "full_discovery"
  | "onboarding_role_confirmation"
  | "onboarding_access_code"
  | "document_limit"
  | "document_limit_full"
  | "get_authorized"
  | "find_assets"
  | "life_insurance"
  | "client_invite_plan"
  | "client_invite_addons"
  | "style_guide"
  | "upgrade_link";
export type AnalyticsErrorType =
  | "network"
  | "invalid_code"
  | "expired_code"
  | "rate_limited"
  | "timeout"
  | "not_found"
  | "unknown";

const STARTED_AT_KEY_PREFIX = "elayne_onboarding_started_at_";

function readStartedAt(flow: OnboardingFlow): number | null {
  try {
    const raw = localStorage.getItem(`${STARTED_AT_KEY_PREFIX}${flow}`);
    const value = raw ? Number(raw) : NaN;
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

function writeStartedAt(flow: OnboardingFlow): boolean {
  try {
    localStorage.setItem(`${STARTED_AT_KEY_PREFIX}${flow}`, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

export function trackOnboardingStarted(
  flow: OnboardingFlow,
  properties: {
    entry_type?: OnboardingEntryType;
    utm_source?: string | null;
    utm_medium?: string | null;
    utm_campaign?: string | null;
  } = {},
) {
  if (readStartedAt(flow) === null && writeStartedAt(flow)) {
    trackEvent(EventType.ONBOARDING_STARTED, {
      flow,
      entry_type: properties.entry_type,
      utm_source: properties.utm_source || undefined,
      utm_medium: properties.utm_medium || undefined,
      utm_campaign: properties.utm_campaign || undefined,
    });
    setPersonProperties({}, { onboarding_flow: flow });
  }
}

export function getOnboardingDurationSeconds(flow: OnboardingFlow): number | undefined {
  const startedAt = readStartedAt(flow);
  if (startedAt === null) return undefined;
  return Math.max(0, Math.round((Date.now() - startedAt) / 1000));
}

export function trackUserSignedUp(flow: OnboardingFlow, signupMethod: SignupMethod) {
  trackEvent(EventType.USER_SIGNED_UP, { flow, signup_method: signupMethod });
  setPersonProperties({}, { signup_method: signupMethod, signed_up_at: new Date().toISOString() });
}

export function trackOnboardingCompleted(
  flow: OnboardingFlow,
  properties: {
    entry_type?: OnboardingEntryType;
    estate_id?: number | string | null;
    decedent_path?: "obituary" | "manual";
    has_paid?: boolean;
  } = {},
) {
  trackEvent(EventType.ONBOARDING_COMPLETED, {
    flow,
    entry_type: properties.entry_type,
    estate_id: properties.estate_id != null ? String(properties.estate_id) : undefined,
    decedent_path: properties.decedent_path,
    has_paid: properties.has_paid,
    total_duration_seconds: getOnboardingDurationSeconds(flow),
  });
  setPersonProperties({}, { onboarding_completed_at: new Date().toISOString() });
}

export function getOnboardingFlow(answers: FlowAnswers): OnboardingFlow {
  return answers["is-invited-member"] === "true" ? "family_invite" : "self_serve";
}

export function getEntryType(answers: FlowAnswers): OnboardingEntryType {
  const entryType = answers["entry-type"];
  if (entryType === "access-code") return "access_code";
  if (entryType === "obituary") return "obituary_link";
  return "default";
}

export function getDecedentPath(answers: FlowAnswers): "obituary" | "manual" | undefined {
  const hasObituary = answers["has-obituary"];
  if (hasObituary === "yes") return "obituary";
  if (hasObituary === "no") return "manual";
  return undefined;
}

export function toErrorType(error: unknown): AnalyticsErrorType {
  const message = (
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : typeof error === "object" && error !== null && "message" in error
          ? String((error as { message: unknown }).message)
          : ""
  ).toLowerCase();

  if (!message) return "unknown";
  if (message.includes("expired")) return "expired_code";
  if (message.includes("invalid") && message.includes("code")) return "invalid_code";
  if (message.includes("rate") || message.includes("too many")) return "rate_limited";
  if (message.includes("timeout") || message.includes("timed out")) return "timeout";
  if (message.includes("not found") || message.includes("404")) return "not_found";
  if (message.includes("network") || message.includes("fetch")) return "network";
  return "unknown";
}

export function getEditedFields(
  original: Record<string, string | null | undefined>,
  edited: Record<string, string | null | undefined>,
): string[] {
  return Object.keys(edited).filter(
    (field) => (original[field] ?? "").trim() !== (edited[field] ?? "").trim(),
  );
}

export function getFileTypes(fileNames: string[]): string {
  const extensions = fileNames
    .map((name) => (name.includes(".") ? name.split(".").pop()?.toLowerCase() : undefined))
    .filter((ext): ext is string => !!ext);
  return Array.from(new Set(extensions)).join(",");
}

export function secondsSince(startedAt: number): number {
  return Math.max(0, Math.round((Date.now() - startedAt) / 1000));
}
